import * as React from "react";
import { useBloomreachAppointmentContent } from "@grandvisionhq/bloomreach";
import { Types } from "@grandvisionhq/graphql";
import { useIntl } from "@grandvisionhq/state";
import { useTrackPageView } from "@grandvisionhq/svc-tealium/events";
import { getDirectionsUrl, DateUtils } from "@grandvisionhq/utils";
import { useAddToCalendarDlEventDispatcher } from "../../../../../data-layer/hooks/v3/data-layer/hooks/use-add-to-calendar-dl-event-dispatcher.js";
import { useAppointmentSlots } from "../../../../../pages/BookAppointment/context/v3/pages/BookAppointment/context/appointment-slots.js";
import { useAppointmentContext } from "../../../../../pages/BookAppointment/context/v3/pages/BookAppointment/context/AppointmentContext.js";
import { usePageData } from "../../../../../pages/BookAppointment/context/v3/pages/BookAppointment/context/PageDataContext.js";
import AppointmentConfirmation from "./AppointmentConfirmation.js";
import useVirtualAppointment from "../../../../../pages/BookAppointment/hooks/v3/pages/BookAppointment/hooks/use-virtual-appointment.js";
import useStoreOfferById from "../../../../../hooks/api/v3/hooks/api/use-store-offer-by-id.js";
import { clearStoredAffiliateId } from "../../../../../utils/v3/utils/affiliate-id-storage.js";
const emptyArray = [];
const AppointmentConfirmationContainer = () => {
  const { getLabel, getFormattedLabel } = useIntl();
  const [, { store, type, timeSlot, appointment: baseAppointment }] = useAppointmentContext();
  const { googleMapsApiKey, storesUrl, opco, appointmentTypes, offerFallbackImageUrl } = usePageData();
  const [{ isVirtualAppointmentType }] = useVirtualAppointment();
  const [getStoreOfferByCode, storeOfferByCode] = useStoreOfferById({ globalStoreId: "0" });
  const { Slots } = useAppointmentSlots();
  const { appointmentDisplayType } = useBloomreachAppointmentContent();
  const labels = React.useMemo(
    () => ({
      heading: getFormattedLabel(
        `appointments.component.appointmentConfirmation.${baseAppointment?.isReservation ? "reservationHeading" : "heading"}`,
        {
          labelData: { name: baseAppointment?.invitee?.firstName ?? "" }
        }
      ),
      subHeading: getLabel(
        `appointments.component.appointmentConfirmation.${baseAppointment?.isReservation ? "reservationSubheading" : "subHeading"}`
      ),
      notification: getLabel(
        `appointments.component.appointmentConfirmation.${baseAppointment?.preferredChannelOfCommunication === Types.AppointmentCommunicationChannel.EMAIL ? "emailNotification" : "smsNotification"}`
      ),
      addToCalendar: getLabel("appointments.component.appointmentConfirmation.addToCalendar"),
      appointment: getLabel("appointments.component.appointmentConfirmation.appointment"),
      where: getLabel("appointments.component.appointmentConfirmation.where"),
      who: getLabel("appointments.component.appointmentConfirmation.who"),
      getDirections: getLabel("appointments.component.appointmentConfirmation.getDirections"),
      cantMakeIt: getLabel("appointments.component.appointmentConfirmation.cantMakeIt"),
      cancelOrReschedule: getFormattedLabel(
        "appointments.component.appointmentConfirmation.cancelOrReschedule",
        { parseHTML: true, parseLink: true }
      ),
      exploreProducts: getLabel("appointments.component.appointmentConfirmation.exploreProducts"),
      tryInStoreProducts: getLabel(
        "appointments.component.appointmentConfirmation.tryInStoreProducts.heading"
      ),
      tryInStoreProductsDisclaimer: getLabel(
        "appointments.component.appointmentConfirmation.tryInStoreProducts.disclaimer"
      )
    }),
    [baseAppointment, getLabel, getFormattedLabel]
  );
  const appointment = React.useMemo(
    () => baseAppointment && type ? {
      ...baseAppointment,
      appointmentType: type
    } : null,
    [baseAppointment, type]
  );
  const directionsUrl = store ? getDirectionsUrl({ streetName: store.streetName, town: store.town, opco }) : void 0;
  const appointmentConfirmationContent = React.useMemo(() => {
    const appointmentContent = appointmentDisplayType === "group" ? appointmentTypes?.reduce(
      (acc, appointmentGroup) => {
        appointmentGroup.appointmentTypes.forEach((entry) => {
          if (entry.appointmentConfirmationContent) {
            acc[entry.typeId] = entry.appointmentConfirmationContent;
          }
        });
        return acc;
      },
      {}
    ) : appointmentTypes?.find((content) => content.typeId === type?.id);
    return appointmentContent?.appointmentConfirmationContent;
  }, [appointmentTypes, type?.id]);
  const aabConfirmationPageView = React.useCallback(
    () => new Promise((resolve) => {
      if (!store || !type || !appointment || !timeSlot) {
        return;
      }
      resolve({
        id: "VirtualPage-View",
        Page_Type: "Confirmation",
        Page_Section1: "Exam",
        Store_Search_StoreId: store.globalStoreId,
        Exam_Fitting: type.name,
        Exam_Id: appointment.id,
        Exam_AppointmentDate: DateUtils.toLocalDate(timeSlot.from)
      });
    }),
    [store, type, appointment, timeSlot]
  );
  useTrackPageView(aabConfirmationPageView);
  React.useEffect(() => {
    if (!store) {
      return;
    }
    getStoreOfferByCode({
      variables: {
        globalStoreId: store.globalStoreId
      }
    });
  }, [getStoreOfferByCode, appointment, store]);
  const { pushAddToCalendarDlEvent } = useAddToCalendarDlEventDispatcher({
    appointmentCondition: "new",
    type,
    store,
    isVirtualAppointment: isVirtualAppointmentType(type),
    timeSlot
  });
  React.useEffect(() => {
    clearStoredAffiliateId();
  }, []);
  React.useEffect(() => {
    Slots.clearTryInStoreStorage?.();
  }, [Slots]);
  if (!store || !type || !timeSlot || !appointment) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(
    AppointmentConfirmation,
    {
      labels,
      store,
      offers: storeOfferByCode.data ?? emptyArray,
      type,
      timeSlot,
      appointment,
      isVirtualAppointmentType: isVirtualAppointmentType(type),
      googleMapsApiKey: !isVirtualAppointmentType(type) ? googleMapsApiKey : void 0,
      storesUrl: !isVirtualAppointmentType(type) ? storesUrl : void 0,
      directionsUrl,
      appointmentConfirmationContent,
      offerFallbackImageUrl,
      onICSButtonClick: pushAddToCalendarDlEvent,
      tryInStoreProducts: appointment.products
    }
  );
};
export {
  AppointmentConfirmationContainer as default
};
//# sourceMappingURL=AppointmentConfirmationContainer.js.map
